import { createContext, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMe,
  logout as logoutApi,
  AuthOrganisation,
} from "@/lib/Auth/authApi";
import { useAuthStore } from "@/lib/Auth/authStore";
import { organisationsQueryOptions } from "@/lib/Auth/organisationsQuery";

type AuthContextValue = {
  token: string | null;
  organisations: AuthOrganisation[];
  isSignedIn: boolean;
  signOut: (options?: { redirectUrl?: string }) => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { token, clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  // Non-suspense: the app renders immediately (org/supplier come from the URL).
  // me/organisations load in parallel and only power the org switcher, admin
  // checks, and route guards — they must not block the first paint.
  const { data: meData } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      if (!token) return null;

      try {
        return await getMe(token);
      } catch (error) {
        if (error instanceof Error && error.message === "Session expired") {
          clearAuth();
        }
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: orgData } = useQuery(organisationsQueryOptions(token));

  const organisations = orgData?.organisations ?? [];
  // Optimistic: a token means signed in until /me resolves and says otherwise,
  // so guards don't flash signed-out while /me is still in flight.
  const meResolvedSignedOut = meData !== undefined && !meData?.user;
  const isSignedIn = !!token && !meResolvedSignedOut;

  async function signOut(options?: { redirectUrl?: string }) {
    if (token) {
      await logoutApi(token);
    }
    clearAuth();
    queryClient.removeQueries({ queryKey: ["auth"] });
    if (options?.redirectUrl) {
      window.location.href = options.redirectUrl;
    }
  }

  return (
    <AuthContext
      value={{
        token,
        organisations,
        isSignedIn,
        signOut,
      }}
    >
      {children}
    </AuthContext>
  );
}
